<template>
    <pc-page-layout
        v-bind="layoutBinder"
        ref="layout">
        <template v-if="!loading">
            <pc-page-header icon-class="menu-customizations" />
            <h3>{{ connectionNameLabel }} {{ Model.Name }}</h3>
            <div
                v-for="(group, gIndex) in inputPropertyGroups"
                v-bind:key="gIndex">
                <hr />
                <h3 class="mb-3">
                    {{ group.perConfiguration ? perConfigurationInputPropertiesTitle : inputPropertiesTitle }}
                </h3>
                <div
                    v-for="(property, index) in group.properties"
                    v-bind:key="index"
                    class="col col-12 col-lg-6">
                    <pc-transition transition="slide">
                        <connection-property-editor
                            v-if="inputPropertyVisibility[property.PublicId]"
                            v-model="property.Value"
                            v-bind:authorization-header-in-use="authorizationHeaderInUse"
                            v-bind:property="property"
                            v-bind:disabled="!isPropertyEditable(property)"
                            v-bind:can-show-sensitive-values="
                                Model.CanSaveProperties && property.PropertyType !== configurablePropertyType
                            "
                            v-bind:show-help="Boolean(property.InputHelp)"
                            v-bind:alternate-validation-hub="propertyValidationHub"
                            v-on:property-changed="onPropertiesChanged($event)"
                            v-on:get-plaintext-value-requested="getPlaintextValueRequested"
                            v-on:help-clicked="property.helpShown = !property.helpShown"
                            v-on:validated="
                                onValidated(`${propertyValidationKeyPrefix}${property.PublicId}`, $event)
                            " />
                    </pc-transition>
                    <pc-alert v-model:shown="property.helpShown">
                        {{ property.InputHelp }}
                    </pc-alert>
                    <div
                        v-if="shouldShowFullUrlPreviewForProperty(property)"
                        class="alert alert-info">
                        <preview-web-adapter-url
                            v-bind:is-prod-preview="checkIsProdBaseUriProperty(property)"
                            v-bind:base-uri="property.Value"
                            v-bind:relative-uri="Model.RelativeUri" />
                    </div>
                </div>
                <div
                    v-if="Model.HttpMethod"
                    class="col col-12 col-lg-6">
                    <pc-transition transition="slide">
                        <pc-select
                            v-model="Model.HttpMethod"
                            class="mb-3"
                            disabled
                            v-bind:label="httpMethodLabel"
                            v-bind:options="[
                                {
                                    text: Model.HttpMethod,
                                    value: Model.HttpMethod
                                }
                            ]" />
                    </pc-transition>
                    <pc-alert v-bind:shown="canOnlySupportQueryParameters">
                        {{ httpMethodQueryParameterText }}
                    </pc-alert>
                </div>
                <div
                    v-if="Model.PayloadType"
                    class="col col-12 col-lg-6">
                    <pc-transition transition="slide">
                        <pc-select
                            v-model="Model.PayloadType"
                            class="mb-3"
                            disabled
                            v-bind:label="payloadTypeLabel"
                            v-bind:options="[
                                {
                                    text: Model.PayloadType,
                                    value: Model.PayloadType
                                }
                            ]" />
                    </pc-transition>
                </div>
                <div
                    v-if="!group.perConfiguration && Model.CanSaveProperties"
                    class="col col-12 col-lg-6">
                    <pc-button
                        id="savePropertiesButton"
                        primary
                        class="mt-3 mb-3"
                        v-bind:disabled="hasPropertyErrors || saving"
                        v-on:click="saveProperties">
                        {{ savePropertiesButtonLabel }}
                    </pc-button>
                    <span v-if="saving">
                        <span class="fst-italic ms-1">{{ savingLabel }}</span>
                    </span>
                    <span v-else-if="saved">
                        <span class="fst-italic ms-1">{{ savedLabel }}</span>
                        <span class="text-success">&#x2714;</span>
                    </span>
                    <span v-else-if="unsavedChanges">
                        <span class="fst-italic ms-1">{{ unsavedChangesLabel }}</span>
                    </span>
                </div>
            </div>
            <hr v-if="Model.PathParameterFields.length > 0" />
            <div
                v-if="Model.PathParameterFields.length > 0"
                class="d-flex">
                <h3>{{ pathParametersTitle }}</h3>
            </div>
            <div class="col col-12 col-lg-6">
                <div
                    v-for="field in Model.PathParameterFields"
                    v-bind:key="field.PublicId">
                    <pc-input
                        v-model="field.Value"
                        v-bind:validator="getFieldValidator(field)"
                        v-bind:data-test-role="`testRole.pathparameter.${field.DisplayName}`"
                        class="mb-3"
                        v-bind:label="field.DisplayName"
                        v-bind:required="field.IsRequired"
                        v-on:validated="onValidated(`pathParameterValue${field.PublicId}`, $event)" />
                </div>
            </div>
            <hr v-if="Model.InputFields.length > 0" />
            <div
                v-if="Model.InputFields.length > 0"
                class="d-flex">
                <h3>{{ requestFieldsTitle }}</h3>
            </div>
            <div class="col col-12 col-lg-6">
                <div
                    v-for="(field, index) in Model.InputFields"
                    v-bind:key="field.PublicId">
                    <!-- Dummy element for attachments. Testing attachments is not currently supported -->
                    <div v-if="field.Type === 'Attachment'" />
                    <div v-else-if="field.Type === 'User'">
                        <pc-label
                            v-bind:for-id="`id.requestfield.${field.DisplayName}`"
                            v-bind:show-required="field.IsRequired"
                            v-bind:label="field.DisplayName">
                            <span class="fst-italic"> {{ getInputFieldLabel(field) }} </span>
                        </pc-label>
                        <pc-select
                            v-bind:id="`id.field.${field.DisplayName}`"
                            v-model="field.Value"
                            v-bind:data-test-role="`testRole.field.${field.DisplayName}`"
                            class="mb-3"
                            v-bind:label="getInputFieldLabel(field)"
                            v-bind:required="field.IsRequired"
                            v-bind:options="usersOptions" />
                    </div>
                    <div v-else-if="field.IsMultiInstance">
                        <test-connection-request-field
                            v-bind:key="`requestField${field.PublicId}`"
                            v-model="Model.InputFields[index]"
                            v-bind:input-field-label="getInputFieldLabel(field)"
                            v-bind:field-validator="getFieldValidator(field)"
                            v-on:validated="onValidated"
                            v-on:element-removed="onElementRemoved" />
                    </div>
                    <div v-else>
                        <pc-label
                            v-bind:for-id="`id.requestfield.${field.DisplayName}`"
                            v-bind:show-required="field.IsRequired"
                            v-bind:label="field.DisplayName">
                            <span class="fst-italic"> {{ getInputFieldLabel(field) }} </span>
                        </pc-label>
                        <pc-input
                            v-bind:id="`id.requestfield.${field.DisplayName}`"
                            v-model="field.Value"
                            v-bind:validator="getFieldValidator(field)"
                            v-bind:data-test-role="`testRole.field.${field.DisplayName}`"
                            class="mb-3"
                            v-bind:required="field.IsRequired"
                            v-on:validated="onValidated(`inputValue${field.PublicId}`, $event)" />
                    </div>
                </div>
            </div>
            <hr v-if="Model.AllowDynamicInputs" />
            <div v-if="Model.AllowDynamicInputs">
                <h3>{{ dynamicRequestFieldsLabel }}</h3>
                <div class="col col-12 col-lg-6">
                    <div
                        v-for="(field, index) in dynamicInputFields"
                        v-bind:key="`dynamicInputField${index}`">
                        <div class="d-flex align-items-center">
                            <pc-input
                                v-model="field.Name"
                                v-bind:label="dynamicFieldNameInputLabel"
                                class="flex-grow-1"
                                v-bind:validator="dynamicInputFieldNameValidator"
                                v-on:validated="onValidated(`dynamicInputName${index}`, $event)"
                                v-on:update:model-value="delayValidate()" />
                            <img
                                src="~Images/icons/trash.svg"
                                class="capsule-icon ms-2 me-1 mt-4"
                                v-on:click="removeDynamicInputField(index)" />
                        </div>
                        <pc-input
                            v-model="field.Value"
                            v-bind:label="dynamicFieldValueInputLabel"
                            class="flex-grow-1 mb-3" />
                    </div>
                    <pc-button
                        class="mt-3"
                        primary
                        v-on:click="addDynamicInputField">
                        {{ addDynamicRequestFieldButtonLabel }}
                    </pc-button>
                </div>
            </div>
            <hr />
            <pc-alert
                v-model:shown="Model.ShowUserWarning"
                v-bind:severity="programAdminAlertSeverity">
                {{ programAdminWarning }}
            </pc-alert>
            <pc-alert
                v-model:shown="Model.ShowCPAdminWarning"
                v-bind:severity="cprAdminAlertSeverity">
                {{ cprAdminWarning }}
            </pc-alert>
            <pc-loader v-bind:loading="testConnectionLoading">
                <div class="col col-12 col-lg-6">
                    <pc-button
                        id="callConnectionButton"
                        primary
                        class="mt-3 mb-3"
                        v-bind:disabled="!canCallConnection || testConnectionLoading"
                        v-on:click="testConnection">
                        {{ callConnectionButtonLabel }}
                    </pc-button>
                </div>
                <div
                    v-if="!testConnectionLoading"
                    class="col">
                    <div
                        v-bind:id="statusAlertId"
                        v-bind:class="statusTextClass">
                        <div>
                            <h5>{{ statusMessageLabel }} {{ Model.ErrorMessage }}</h5>
                        </div>
                        <div v-if="Model.ErrorDetail">
                            <h6>{{ statusDetailLabel }} {{ Model.ErrorDetail }}</h6>
                        </div>
                    </div>
                    <pc-input
                        v-if="Model.Url"
                        id="RequestUrl"
                        v-model="Model.Url"
                        class="mt-3"
                        v-bind:disabled="true"
                        v-bind:label="requestUrlLabel" />
                    <pc-textarea
                        v-if="prettyFormattedRequest"
                        id="RequestMessage"
                        v-model="prettyFormattedRequest"
                        class="mt-3"
                        v-bind:disabled="true"
                        v-bind:label="requestMessageLabel" />
                    <pc-textarea
                        v-if="prettyFormattedResponse"
                        id="ResponseMessage"
                        v-model="prettyFormattedResponse"
                        class="mt-3"
                        v-bind:disabled="true"
                        v-bind:label="responseMessageLabel" />
                </div>
            </pc-loader>
            <hr v-if="Model.OutputFields.length > 0" />
            <div
                v-if="Model.OutputFields.length > 0"
                class="d-flex">
                <h3>{{ responseFieldsTitle }}</h3>
            </div>
            <div class="col col-12 col-lg-6">
                <div
                    v-for="field in Model.OutputFields"
                    v-bind:key="field.PublicId">
                    <!-- Dummy element for attachments. Testing attachments is not currently supported -->
                    <div v-if="field.Type === fieldTypeAttachment" />
                    <pc-select
                        v-else-if="field.Type === fieldTypeUser"
                        v-model="field.Value"
                        v-bind:data-test-role="`testRole.field.${field.DisplayName}`"
                        class="mb-3"
                        v-bind:disabled="true"
                        v-bind:label="field.DisplayName"
                        v-bind:options="usersOptions" />
                    <div v-else-if="field.IsMultiInstance">
                        <pc-label
                            v-bind:for-id="_getMultiInstanceEmptyInputId(field)"
                            v-bind:show-required="field.IsRequired"
                            v-bind:label="field.DisplayName">
                            <span class="fst-italic"> {{ getInputFieldLabel(field) }} </span>
                        </pc-label>
                        <pc-input
                            v-if="field.MultiInstanceValues.length === 0"
                            v-bind:id="`${field.PublicId}_emptyPlaceholder`"
                            v-bind:disabled="true"
                            class="mb-3" />
                        <pc-input
                            v-for="(_, index) in field.MultiInstanceValues"
                            v-bind:key="`outputValue${field.PublicId}_${index}`"
                            v-model="field.MultiInstanceValues[index]"
                            class="mb-3"
                            v-bind:placeholder="multiInstanceElementPlaceholder"
                            v-bind:disabled="true"
                            v-bind:label="getInputFieldLabel(field)" />
                    </div>
                    <pc-input
                        v-else
                        v-model="field.Value"
                        v-bind:data-test-role="`testRole.field.${field.DisplayName}`"
                        class="mb-3"
                        v-bind:disabled="true"
                        v-bind:label="field.DisplayName" />
                </div>
            </div>
            <div
                v-if="Model.AllowDynamicOutputs && dynamicOutputFields.length > 0"
                class="mb-3">
                <hr />
                <h3>{{ dynamicResponseFieldsLabel }}</h3>
                <div
                    v-for="(field, index) in dynamicOutputFields"
                    v-bind:key="`dynamicOutputField${index}`"
                    class="col col-12 col-lg-6">
                    <pc-input
                        v-model="field.Name"
                        v-bind:label="dynamicFieldNameInputLabel"
                        v-bind:disabled="true"
                        class="flex-grow-1" />
                    <pc-input
                        v-model="field.Value"
                        v-bind:label="dynamicFieldValueInputLabel"
                        v-bind:disabled="true"
                        class="flex-grow-1 mb-3" />
                </div>
            </div>
            <hr />
            <div class="d-flex align-items-center justify-content-end">
                <pc-button
                    id="closeButton"
                    class="me-2"
                    v-on:click="closePage">
                    {{ closeButtonLabel }}
                </pc-button>
            </div>
        </template>
    </pc-page-layout>
</template>

<script>
    import pageBase from "Components/page/_page-base.js";
    import {
        PROPERTY_TYPES,
        DYNAMIC_FIELD_NAME_MAX_LENGTH,
        CONNECTION_FIELD_TYPES,
        getValidatorTypesForFieldType,
        parseVisibilityCondition,
        CONNECTION_FIELD_LOCATION,
        CONNECTION_HTTP_METHODS
    } from "Util/webAdapterUtil";
    import connectionPropertyEditor from "./Components/connectionPropertyEditor.vue";
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import { ValidationHub } from "Validation/validationHub";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/testConnection.i18n";
    import { areStringsEqualIgnoreCase } from "Util/util.js";
    import { ALERT_SEVERITY } from "Util/alerts";
    import previewWebAdapterUrl from "./previewWebAdapterUrl.vue";
    import testConnectionRequestField from "./Components/testConnectionMultiInstanceRequestField.vue";

    export const PROD_BASE_URI_PROPERTY_PUBLIC_ID = "ProductionURL";
    export const TEST_BASE_URI_PROPERTY_PUBLIC_ID = "TestURL";

    export default {
        name: "TestConnection",
        components: {
            connectionPropertyEditor,
            previewWebAdapterUrl,
            testConnectionRequestField
        },
        extends: pageBase,
        provide() {
            return {
                validationHub: this.validationHub
            };
        },
        setup: pageBase.setup,
        data: function () {
            return {
                rowKeyPropName: "PublicId",
                saved: false,
                saving: false,
                unsavedChanges: false,
                dynamicInputFields: [],
                dynamicOutputFields: [],
                inputPropertyGroups: [],
                inputPropertyVisibility: {},
                statusAlertId: "connectionStatusAlertPrimary",
                statusTextClass: "alert alert-primary",
                programAdminAlertSeverity: ALERT_SEVERITY.warning,
                cprAdminAlertSeverity: ALERT_SEVERITY.error,
                testConnectionLoading: false,

                validationResults: {},
                validationHub: new ValidationHub(),
                propertyValidationHub: new ValidationHub(),
                nameValidator: getValidator(this.$app, VALIDATOR_TYPES.required),
                hasPropertyErrors: false,
                propertyValidationKeyPrefix: "property",

                authenticationPropertyPublicId: "AuthenticationType",
                authenticationDefaultValue: "None",

                configurablePropertyType: PROPERTY_TYPES.readWrite,

                fieldTypeUser: CONNECTION_FIELD_TYPES.user,
                fieldTypeAttachment: CONNECTION_FIELD_TYPES.attachment
            };
        },
        computed: {
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            ),
            authorizationHeaderInUse() {
                const inputProperties = this.Model.InputProperties;
                for (const index in inputProperties) {
                    if (inputProperties[index].PublicId === this.authenticationPropertyPublicId) {
                        return (
                            inputProperties[index].Value !== this.authenticationDefaultValue &&
                            Boolean(inputProperties[index].Value)
                        ); // In case the user has not made a selection
                    }
                }
                return false;
            },
            usersOptions() {
                return [{ text: this.defaultUserLabel, value: "" }].concat(
                    this.Model.AdminUsers.map((x) => {
                        return {
                            text: x.Name,
                            value: x.UserId
                        };
                    })
                );
            },
            canCallConnection() {
                for (const key in this.validationResults) {
                    if (this.validationResults.hasOwnProperty(key)) {
                        if (this.validationResults[key].hasError) {
                            return false;
                        }
                    }
                }
                return true;
            },
            dynamicInputFieldNameValidator() {
                return getValidator(this.$app, [
                    VALIDATOR_TYPES.required,
                    {
                        type: VALIDATOR_TYPES.custom,
                        validate: this._isDynamicFieldNameNotDuplicate,
                        errorMsg: this.duplicateNameValidationError,
                        skipTrimValue: true
                    },
                    {
                        type: VALIDATOR_TYPES.maxLength,
                        maxLength: DYNAMIC_FIELD_NAME_MAX_LENGTH
                    }
                ]);
            },
            canOnlySupportQueryParameters() {
                return (
                    this.Model.HttpMethod === CONNECTION_HTTP_METHODS.GET ||
                    this.Model.HttpMethod === CONNECTION_HTTP_METHODS.DELETE
                );
            },
            prettyFormattedRequest() {
                return this.prettyFormatPayloadString(this.Model?.Request);
            },
            prettyFormattedResponse() {
                return this.prettyFormatPayloadString(this.Model?.Response);
            }
        },
        methods: {
            onLoaded() {
                for (const prop of this.Model.InputProperties) {
                    // Input properties are grouped by whether they are per-configuration or per-connection
                    const perConfiguration = prop.PropertyType === this.configurablePropertyType;
                    let group = this.inputPropertyGroups.find((g) => g.perConfiguration === perConfiguration);

                    if (!group) {
                        // This is the first property in the group, so create the group
                        group = {
                            perConfiguration: perConfiguration,
                            properties: []
                        };
                        this.inputPropertyGroups.push(group);
                    }

                    group.properties.push(prop);
                }

                this.updateInputPropertyVisibility();

                for (const inputField of this.Model.InputFields) {
                    if (inputField.IsMultiInstance) {
                        inputField.MultiInstanceValues = [""];
                    }
                }

                for (const outputField of this.Model.OutputFields) {
                    if (outputField.IsMultiInstance) {
                        outputField.MultiInstanceValues = [];
                    }
                }
            },
            updateInputPropertyVisibility() {
                for (const prop of this.Model.InputProperties) {
                    let isVisible = true;
                    const condition = parseVisibilityCondition(prop.VisibilityCondition);
                    if (condition) {
                        const otherProp = this.Model.InputProperties.find((p) => p.PublicId === condition.propName);
                        isVisible = condition.propValues.includes(otherProp.Value);
                    }
                    this.inputPropertyVisibility[prop.PublicId] = isVisible;
                    const validationKey = `${this.propertyValidationKeyPrefix}${prop.PublicId}`;
                    if (!isVisible && this.validationResults[validationKey]) {
                        // If there is an invalid value in a property that is being hidden, remove
                        // the error so the user can save without having to clear the value first
                        this.onValidated(validationKey, { hasError: false });
                    }
                }
            },
            onValidated(key, result) {
                const updatedResults = this._clone(this.validationResults);
                updatedResults[key] = result;
                let hasPropertyErrors = false;
                for (const _key in updatedResults) {
                    if (_key.startsWith("property") && updatedResults[_key].hasError) {
                        hasPropertyErrors = true;
                    }
                }
                this.hasPropertyErrors = hasPropertyErrors;
                this.validationResults = updatedResults;
            },
            onElementRemoved(key) {
                const updatedResults = this._clone(this.validationResults);
                delete updatedResults[key];
                this.validationResults = updatedResults;
            },
            async saveProperties() {
                this.propertyValidationHub.validate();
                if (this.hasPropertyErrors) {
                    return;
                }

                this.saved = false;
                this.saving = true;
                this.unsavedChanges = false;

                try {
                    for (const property of this.Model.InputProperties) {
                        if (!this.inputPropertyVisibility[property.PublicId]) {
                            // For hidden properties, set them to the default value
                            // because users expect data for hidden properties not to be saved.
                            property.Value = property.DefaultValue;
                        }
                    }

                    await this.SaveInputProperties2({
                        programId: this.ProgramId,
                        connectionModel: this.Model
                    });
                    this.$app.setUnsavedChanges(false);
                    if (!this.unsavedChanges) {
                        // Don't say it's saved if the user made changes while we're saving
                        this.saved = true;
                    }
                } finally {
                    this.saving = false;
                }

                // Make the saved indicator go away after 4 seconds
                await this.$app.timeout(4000);
                this.saved = false;
            },
            onPropertiesChanged(isPerConfiguration) {
                // Per-configuration properties are not saved in the Test Connection page
                if (!isPerConfiguration && this.Model.CanSaveProperties) {
                    this.saved = false;
                    this.unsavedChanges = true;
                    this.$app.setUnsavedChanges(true);
                }
                this.updateInputPropertyVisibility();
            },
            isPropertyEditable(property) {
                if (!this.inputPropertyVisibility[property.PublicId]) {
                    // Property is not editable if it's not even visible
                    return false;
                }
                if (property.PropertyType === this.configurablePropertyType || this.Model.CanEditAllProperties) {
                    return true;
                }
                return this.Model.CanSaveProperties;
            },
            getInputFieldLabel(field) {
                if (this.canOnlySupportQueryParameters) {
                    return "";
                }
                if (field.FieldLocation == CONNECTION_FIELD_LOCATION.payload) {
                    return this.payloadIndicatorText;
                } else if (field.FieldLocation == CONNECTION_FIELD_LOCATION.queryParameters) {
                    return this.queryParamIndicatorText;
                }
                return "";
            },
            async getPlaintextValueRequested(prop, successCallback, cancelCallback) {
                try {
                    const result = await this.GetUnencryptedPropertyValue({
                        programId: this.ProgramId,
                        connectionId: this.Model.PublicId,
                        propertyName: prop.PublicId
                    });
                    if (result.status === "success") {
                        successCallback(result.Data);
                    } else {
                        cancelCallback();
                    }
                } catch (ex) {
                    cancelCallback();
                }
            },
            testConnection() {
                this.propertyValidationHub.validate();
                this.validationHub.validate();
                if (!this.canCallConnection) {
                    return;
                }

                this.testConnectionLoading = true;
                const dynamicInputs = {};
                for (const item of this.dynamicInputFields) {
                    dynamicInputs[item.Name] = item.Value;
                }

                this._clearOutput();
                this.CallConnection({
                    programId: this.ProgramId,
                    connectionModel: this.Model,
                    dynamicInputs: dynamicInputs
                }).then(
                    (result) => {
                        for (let i = 0; i < this.Model.OutputFields.length; i++) {
                            if (this.Model.OutputFields[i].IsMultiInstance) {
                                this.Model.OutputFields[i].MultiInstanceValues = result.Data.OutputValues[i];
                            } else {
                                this.Model.OutputFields[i].Value = result.Data.OutputValues[i];
                            }
                        }

                        for (const key in result.Data.DynamicOutputValues) {
                            this.dynamicOutputFields.push({
                                Name: key,
                                Value: result.Data.DynamicOutputValues[key]
                            });
                        }

                        if (result.Data.ErrorMessage || result.Data.ErrorDetail) {
                            this.statusTextClass = "alert alert-danger";
                            this.statusAlertId = "connectionStatusAlertDanger";
                            this.Model.ErrorMessage = result.Data.ErrorMessage;
                            this.Model.ErrorDetail = result.Data.ErrorDetail;
                        } else if (result.Data.WarningMessage || result.Data.WarningDetail) {
                            this.statusTextClass = "alert alert-warning";
                            this.statusAlertId = "connectionStatusAlertWarning";
                            this.Model.ErrorMessage = result.Data.WarningMessage;
                            this.Model.ErrorDetail = result.Data.WarningDetail;
                        } else {
                            this.statusTextClass = "alert alert-success";
                            this.statusAlertId = "connectionStatusAlertSuccess";
                            this.Model.ErrorMessage = "Success";
                            this.Model.ErrorDetail = "";
                        }

                        this.testConnectionLoading = false;
                        this.Model.Request = result.Data.Request;
                        this.Model.Response = result.Data.Response;
                        this.Model.Url = result.Data.Url;
                        return result.Data;
                    },
                    () => {
                        this.testConnectionLoading = false;
                    }
                );
                this.Model.ErrorMessage = "";
                return;
            },
            delayValidate() {
                setTimeout(() => {
                    this.propertyValidationHub.validate();
                    this.validationHub.validate();
                }, 0);
            },
            addDynamicInputField() {
                this.dynamicInputFields.push({ Name: "", Value: "" });
            },
            addDynamicOutputField() {
                this.dynamicOutputFields.push({ Name: "", Value: "" });
            },
            removeDynamicField(list, index) {
                list.splice(index, 1);

                // Reset validation results, so errors from removed fields don't stick around
                this.validationResults = {};
                this.delayValidate();
            },
            removeDynamicInputField(index) {
                this.removeDynamicField(this.dynamicInputFields, index);
            },
            closePage() {
                const url = this.LoadClientModelParameters["connectionsUrl"] + "?ProgramId=" + this.ProgramId;
                this.$app.setHref(url);
            },
            getFieldValidator(field) {
                const validationTypes = getValidatorTypesForFieldType(field.Type);
                if (field.IsRequired) {
                    validationTypes.push(VALIDATOR_TYPES.required);
                }
                return getValidator(this.$app, validationTypes);
            },
            shouldShowFullUrlPreviewForProperty(property) {
                // Only show URL preview for Production and Test base URIs if no validation errors
                return (
                    (property.PublicId === PROD_BASE_URI_PROPERTY_PUBLIC_ID ||
                        property.PublicId === TEST_BASE_URI_PROPERTY_PUBLIC_ID) &&
                    property.Value &&
                    this.Model.RelativeUri &&
                    !this.validationResults[`${this.propertyValidationKeyPrefix}${property.PublicId}`]?.hasError
                );
            },
            checkIsProdBaseUriProperty(property) {
                return property.PublicId === PROD_BASE_URI_PROPERTY_PUBLIC_ID;
            },
            prettyFormatPayloadString(payloadString) {
                if (!payloadString) {
                    return null;
                }

                try {
                    return JSON.stringify(JSON.parse(payloadString), null, 4);
                } catch (ex) {
                    return payloadString;
                }
            },
            _clearOutput() {
                for (let i = 0; i < this.Model.OutputFields.length; i++) {
                    this.Model.OutputFields[i].Value = null;
                }
                this.dynamicOutputFields.splice(0, this.dynamicOutputFields.length);

                this.statusTextClass = "alert alert-primary";
                this.statusAlertId = "connectionStatusAlertPrimary";
                this.Model.ErrorMessage = this.statusWaitingLabel;
                this.Model.ErrorDetail = "";
                this.Model.Request = null;
                this.Model.Response = null;
            },
            _clone(obj) {
                return JSON.parse(JSON.stringify(obj));
            },
            _isDynamicFieldNameNotDuplicate(value) {
                const trimmed = value.trim();
                const list = this.dynamicInputFields;
                let count = 0;
                for (let i = 0; i < list.length; i++) {
                    if (areStringsEqualIgnoreCase(list[i].Name.trim(), trimmed)) {
                        count++;
                    }
                }

                return count < 2;
            },
            _getMultiInstanceEmptyInputId(field) {
                if (field.MultiInstanceValues.length > 0) {
                    // If there are values, the empty placeholder will not be present
                    return null;
                }

                return `${field.PublicId}_emptyPlaceholder`;
            }
        }
    };
</script>

<style lang="scss" scoped>
    .capsule-icon {
        border: 0;
        width: 1.5rem;
        height: 1.5rem;
        filter: invert(100%);
    }
</style>
